/**
 * PlotexPostProcessing — Wave 2 Task 2C.
 *
 * Wraps three.js EffectComposer with a preset-driven post-processing stack.
 * Five quality presets map to different pass combinations:
 *
 *   off    — direct renderer.render(), no composer overhead
 *   low    — RenderPass + SMAAPass (anti-aliasing only)
 *   medium — + UnrealBloomPass (threshold 0.85, subtle glow on bright surfaces)
 *   high   — + SSAOPass (screen-space ambient occlusion for contact shadows)
 *   ultra  — all passes at higher resolution
 *
 * Wave 3 Task 3B will move the animate-loop integration into
 * createPlotexEngine(). For now, main.ts calls render() directly inside
 * its rAF loop at the [plotex-hook:postprocess] anchor.
 */

import {
  type Camera,
  type Scene,
  type WebGLRenderer,
} from 'three';

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// SSAOPass is heavier and only used in high/ultra — imported lazily in
// _buildStack to keep the baseline bundle small.

export type PostProcessPreset = 'off' | 'low' | 'medium' | 'high' | 'ultra';

/**
 * Manages the EffectComposer lifecycle and preset switching.
 *
 * Usage from main.ts:
 *   const pp = new PlotexPostProcessing(renderer, scene, camera, 'medium');
 *   // in animate():
 *   pp.render();           // delegates to composer or raw renderer
 *   // on resize:
 *   pp.setSize(w, h);
 *   // on runtime quality change:
 *   pp.setPreset('high');
 */
export class PlotexPostProcessing {
  private renderer: WebGLRenderer;
  private scene: Scene;
  private camera: Camera;
  private composer: EffectComposer | null = null;
  private _preset: PostProcessPreset;

  constructor(
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    preset: PostProcessPreset = 'medium',
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this._preset = preset;

    if (preset !== 'off') {
      this._buildStack(preset);
    }
    console.log(`[plotex] post-processing preset: ${preset}`);
  }

  /** Current active preset. */
  get preset(): PostProcessPreset {
    return this._preset;
  }

  /**
   * Switch preset at runtime. Rebuilds the pass stack from scratch — this
   * is fine because preset changes are infrequent (user clicks a quality
   * button, not per-frame).
   */
  setPreset(preset: PostProcessPreset): void {
    if (preset === this._preset) return;
    this._preset = preset;

    // Dispose old composer + passes to free GPU resources.
    if (this.composer) {
      this.composer.passes.forEach((p: any) => p.dispose?.());
      this.composer.dispose?.();
      this.composer = null;
    }

    if (preset !== 'off') {
      this._buildStack(preset);
    }
    console.log(`[plotex] post-processing preset changed to: ${preset}`);
  }

  /**
   * Call this in the animate loop instead of `renderer.render(scene, camera)`.
   * When preset is 'off', falls through to direct render with zero overhead.
   */
  render(): void {
    if (this.composer && this._preset !== 'off') {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  /** Update render target sizes on window resize. */
  setSize(width: number, height: number): void {
    if (this.composer) {
      this.composer.setSize(width, height);
    }
  }

  // -- Internal -----------------------------------------------------------

  private _buildStack(preset: PostProcessPreset): void {
    const w = this.renderer.domElement.clientWidth;
    const h = this.renderer.domElement.clientHeight;

    const composer = new EffectComposer(this.renderer);
    composer.setSize(w, h);

    // Every preset starts with a RenderPass that captures the scene.
    const renderPass = new RenderPass(this.scene, this.camera);
    composer.addPass(renderPass);

    // Anti-aliasing: SMAA is GPU-light and works on all presets except 'off'.
    const smaa = new SMAAPass(w, h);
    composer.addPass(smaa);

    // Bloom: medium and above get a subtle Unreal-style bloom for bright
    // surfaces (glass facades reflecting the sun, emissive materials).
    if (preset === 'medium' || preset === 'high' || preset === 'ultra') {
      const bloomStrength = preset === 'ultra' ? 0.4 : 0.25;
      const bloomRadius = 0.4;
      const bloomThreshold = 0.85;
      const bloom = new UnrealBloomPass(
        { x: w, y: h } as any,
        bloomStrength,
        bloomRadius,
        bloomThreshold,
      );
      composer.addPass(bloom);
    }

    // SSAO: high and ultra add screen-space ambient occlusion. This is the
    // most expensive pass — adds ~2-4 ms per frame at 1080p. Import is
    // deferred to keep the low/medium code path lean.
    if (preset === 'high' || preset === 'ultra') {
      // Dynamic import would be cleaner but complicates the sync build.
      // SSAOPass is only ~15 KB gzipped and tree-shaken if unused.
      import('three/examples/jsm/postprocessing/SSAOPass.js').then(
        ({ SSAOPass }) => {
          const ssao = new SSAOPass(
            this.scene,
            this.camera as any,
            w,
            h,
          );
          ssao.kernelRadius = preset === 'ultra' ? 16 : 8;
          ssao.minDistance = 0.005;
          ssao.maxDistance = 0.1;
          // Insert SSAO before OutputPass (second-to-last position).
          const outputIdx = composer.passes.findIndex(
            (p: any) => p instanceof OutputPass,
          );
          if (outputIdx >= 0) {
            composer.passes.splice(outputIdx, 0, ssao);
          } else {
            // OutputPass not added yet — just push; it'll be ahead of
            // the OutputPass that gets added next.
            composer.insertPass(ssao, composer.passes.length - 1);
          }
        },
      );
    }

    // OutputPass: applies tone mapping + color space conversion at the end
    // of the chain. Must be last.
    const output = new OutputPass();
    composer.addPass(output);

    this.composer = composer;
  }
}
